/**
 * Alpine Product Media Component
 *
 * Handles variant-aware media display with:
 * - Variant-specific media from metafields (custom.media)
 * - Fallback to product.images for general media
 * - URL tracking for ?variant= parameter
 * - Syncs with slider for thumbnail pagination
 *
 * Usage:
 *   <div x-data="productMedia({ productId: '123', variants: [...], productImages: [...] })">
 *     <template x-for="(media, i) in displayMedia" :key="media.id || i">
 *       <div x-html="media.html"></div>
 *     </template>
 *   </div>
 *
 * Data structure for variants:
 *   [{ id: '123', title: 'Raw Cacao', media: [{ id, type, url, alt, aspect_ratio, html }] }, ...]
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('productMedia', (config = {}) => ({
    // === State ===
    currentVariantId: null,
    displayMedia: [],
    initialized: false,

    // === Config (from x-data) ===
    productId: config.productId || '',
    variants: config.variants || [],
    productImages: config.productImages || [],
    sliderId: config.sliderId || 'product-images',

    // === Lifecycle ===
    init() {
      // Read variant from URL on page load
      this.currentVariantId = this._getVariantFromUrl();

      // If no variant in URL, use first available variant
      if (!this.currentVariantId && this.variants.length > 0) {
        this.currentVariantId = String(this.variants[0].id);
      }

      // Build initial media list
      this._updateDisplayMedia();

      // Listen for variant selection events (from variant-flavor-selector or variant-picker)
      window.addEventListener('store:variant:selected', (e) => {
        if (e.detail && e.detail.variantId) {
          this.selectVariant(String(e.detail.variantId));
        }
      });

      // Mark initialized
      this.$nextTick(() => {
        this.initialized = true;
      });
    },

    // === Public Methods ===
    selectVariant(variantId) {
      if (this.currentVariantId === variantId) return;

      this.currentVariantId = variantId;
      this._updateDisplayMedia();
      this._updateUrl(variantId);
      this._resetSlider();
    },

    // === Private Methods ===
    _getVariantFromUrl() {
      try {
        const params = new URLSearchParams(window.location.search);
        return params.get('variant');
      } catch (_) {
        return null;
      }
    },

    _updateUrl(variantId) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        history.replaceState({}, '', url.toString());
      } catch (_) {}
    },

    _updateDisplayMedia() {
      // Find current variant
      const variant = this.variants.find(v => String(v.id) === String(this.currentVariantId));
      const variantMedia = variant?.media || [];

      // Combine: variant media first, then product images
      // Filter out product images that are already in variant media (by URL/src)
      const variantUrls = new Set(variantMedia.map(m => m.url || m.src));
      const filteredProductImages = this.productImages.filter(img => {
        return !variantUrls.has(img.url || img.src);
      });

      this.displayMedia = [...variantMedia, ...filteredProductImages];

      // Dispatch event for other components (thumbnails, etc.)
      this.$nextTick(() => {
        this.$el.dispatchEvent(new CustomEvent('product-media:update', {
          bubbles: true,
          detail: {
            variantId: this.currentVariantId,
            media: this.displayMedia,
            productId: this.productId
          }
        }));
      });
    },

    _resetSlider() {
      // Find and reset the slider to first slide
      if (!this.sliderId) return;

      const section = this.$el.closest('[id^="shopify-section-"]') || document;
      const slider = section.querySelector(`#${CSS.escape(this.sliderId)}`);

      if (slider && slider._x_dataStack) {
        const sliderData = slider._x_dataStack[0];
        if (sliderData && typeof sliderData.goToSlide === 'function') {
          sliderData.goToSlide(0);
        }
      }
    },

    // === Computed ===
    get currentVariant() {
      return this.variants.find(v => String(v.id) === String(this.currentVariantId)) || null;
    },

    get hasVariantMedia() {
      return this.currentVariant?.media?.length > 0;
    }
  }));
});

/**
 * Alpine Product Thumbs Component
 *
 * Syncs with productMedia to display thumbnails.
 * Listens for product-media:update events to rebuild thumbnail list.
 *
 * Usage:
 *   <div x-data="productThumbs({ sliderId: 'product-images' })">
 *     <template x-for="(media, i) in media" :key="media.id || i">
 *       <button @click="goTo(i)" :class="currentIndex === i ? 'is-selected' : ''">
 *         <img :src="media.thumb_url" />
 *       </button>
 *     </template>
 *   </div>
 */
document.addEventListener('alpine:init', () => {
  Alpine.data('productThumbs', (config = {}) => ({
    // === State ===
    media: [],
    currentIndex: 0,

    // === Config ===
    sliderId: config.sliderId || 'product-images',

    // === Lifecycle ===
    init() {
      // Listen for media updates
      document.addEventListener('product-media:update', (e) => {
        this.media = e.detail.media || [];
        this.currentIndex = 0;
      });

      // Listen for slider changes
      const section = this.$el.closest('[id^="shopify-section-"]') || document;
      section.addEventListener('slider:change', (e) => {
        if (e.detail.id === this.sliderId) {
          this.currentIndex = e.detail.index;
        }
      });
    },

    // === Public Methods ===
    goTo(index) {
      const section = this.$el.closest('[id^="shopify-section-"]') || document;
      const slider = section.querySelector(`#${CSS.escape(this.sliderId)}`);

      if (slider && slider._x_dataStack) {
        const sliderData = slider._x_dataStack[0];
        if (sliderData && typeof sliderData.goToSlide === 'function') {
          sliderData.goToSlide(index);
          this.currentIndex = index;
        }
      }
    }
  }));
});
